import { useQuery } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type MetricDefinition } from "@/components/admin/XeroMetricInfo";
import XeroPanel, { SCROLLER } from "@/components/admin/XeroPanel";
import { useXeroOrgName, useXeroOrgSubtitle } from "@/hooks/useXeroOrgName";
import { formatNumber, formatWhen } from "@/lib/xeroRunFormat";
import {
  XERO_LAKEHOUSE_QUERY_KEY,
  getXeroLakehouse,
  type XeroLakehouseSummary,
} from "@/services/xeroApi";


const STALE_MS = 5 * 60_000;

const METRICS: MetricDefinition[] = [
  {
    term: "Rows",
    definition:
      "Journal lines currently held for that organisation, one row per line. This is what actually landed, as against the Rows figure on a run, which counts rows that run wrote — re-merging an unchanged journal writes it again, so the two do not have to agree.",
  },
  {
    term: "Journals",
    definition:
      "Distinct journals behind those rows. A journal usually has several lines, so this is always the smaller number.",
  },
  {
    term: "Watermark",
    definition:
      "The highest Xero journal number present in the table. It should match the watermark on the Watermarks tab; a gap means rows landed that the run log did not record, or the reverse.",
  },
  {
    term: "Earliest / Latest",
    definition:
      "The journal date range held. Earliest is bounded by the organisation's retrieval date on a backfill — an organisation with no retrieval date set is pulled in full.",
  },
  {
    term: "Last ingested",
    definition: "When a load last wrote to this organisation's partition.",
  },
  {
    term: "Counted",
    definition:
      "When these figures were read. They are cached for five minutes and are not live — press Refresh after a load to force a re-read.",
  },
];

/** "YYYY-MM-DD"-ish timestamps from Fabric, shown as a date only — the time a
 *  journal was dated carries no useful clock component. */
function formatDay(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB");
}

function Banner({ data }: { data: XeroLakehouseSummary }) {
  if (!data.bronze_error) return null;
  return (
    <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="space-y-1">
        <p>{data.bronze_error}</p>
        {data.stale && (
          <p>
            Showing the last counts that were read successfully, which may be out of
            date.
          </p>
        )}
      </div>
    </div>
  );
}


export default function XeroRowCountsPanel() {
  const orgName = useXeroOrgName();
  const orgSubtitle = useXeroOrgSubtitle();
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: XERO_LAKEHOUSE_QUERY_KEY,
    queryFn: () => getXeroLakehouse(),
    staleTime: STALE_MS,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Counting rows in the lakehouse…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {(error as Error)?.message ?? "Couldn’t read the row counts."}
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const rows = data?.organisations ?? [];

  return (
    <XeroPanel
      title="Row counts"
      blurb={
        <>
          Rows currently held per organisation in{" "}
          <span className="font-mono">{data?.bronze_table}</span>.
        </>
      }
      metrics={METRICS}
      aside={
        <span className="text-xs text-muted-foreground">
          Counted {data ? formatWhen(data.generated_at) : "—"}
        </span>
      }
      onRefresh={() => refetch()}
      isFetching={isFetching}
    >
      {data && <Banner data={data} />}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No rows in the lakehouse yet. The table is created by the first load.
        </div>
      ) : (
        <div className={SCROLLER}>
          <Table>
            <TableHeader>
              <TableRow className="sticky top-0 z-10 bg-muted hover:bg-muted">
                <TableHead>Organisation</TableHead>
                <TableHead className="w-[110px] text-right">Rows</TableHead>
                <TableHead className="w-[100px] text-right">Journals</TableHead>
                <TableHead className="w-[120px] text-right">Watermark</TableHead>
                <TableHead className="w-[110px]">Earliest</TableHead>
                <TableHead className="w-[110px]">Latest</TableHead>
                <TableHead className="w-[170px]">Last ingested</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.tenant_id}>
                  <TableCell className="align-top">
                    <div className="font-medium text-foreground">
                      {orgName(row.tenant_id, row.tenant_name)}
                    </div>
                    {orgSubtitle(row.tenant_id) && (
                      <div className="text-xs text-muted-foreground">
                        {orgSubtitle(row.tenant_id)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-right tabular-nums text-muted-foreground">
                    {formatNumber(row.bronze_rows)}
                  </TableCell>
                  <TableCell className="align-top text-right tabular-nums text-muted-foreground">
                    {formatNumber(row.journals)}
                  </TableCell>
                  <TableCell className="align-top text-right font-mono text-xs text-muted-foreground">
                    {formatNumber(row.max_journal_number)}
                  </TableCell>
                  <TableCell className="align-top text-muted-foreground">
                    {formatDay(row.earliest_journal_date)}
                  </TableCell>
                  <TableCell className="align-top text-muted-foreground">
                    {formatDay(row.latest_journal_date)}
                  </TableCell>
                  <TableCell className="align-top text-muted-foreground">
                    {formatWhen(row.last_ingested_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </XeroPanel>
  );
}
